/**
 * TanStack Query hooks para /v1/adelantos — FWA-PWA-031.
 *
 * Wire outbox heredado pattern PWA-029.
 */
import type { Adelanto, AdelantoCreatePayload, AdelantoUpdatePayload } from '@sisrep/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiResponse, apiGet, apiPatch, apiPost, generateIdempotencyKey } from '../api.js';
import { addOutboxItem } from '../outbox/db.js';

export const adelantosKeys = {
  all: ['adelantos'] as const,
  list: (filters: AdelantosListFilters) => ['adelantos', 'list', filters] as const,
  detail: (id: string) => ['adelantos', 'detail', id] as const,
};

export interface AdelantosListFilters {
  cursor?: string | undefined;
  limit?: number | undefined;
  nina_entity_id?: string | undefined;
  tipo_adelanto?: string | undefined;
  estatus?: string | undefined;
}

interface AdelantosListResponse {
  data: Adelanto[];
  next_cursor: string | null;
  has_more: boolean;
}

function buildListQueryString(filters: AdelantosListFilters): string {
  const params = new URLSearchParams();
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.nina_entity_id) params.set('nina_entity_id', filters.nina_entity_id);
  if (filters.tipo_adelanto) params.set('tipo_adelanto', filters.tipo_adelanto);
  if (filters.estatus) params.set('estatus', filters.estatus);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useAdelantosList(filters: AdelantosListFilters = {}) {
  return useQuery<ApiResponse<AdelantosListResponse>>({
    queryKey: adelantosKeys.list(filters),
    queryFn: () => apiGet<AdelantosListResponse>(`/v1/adelantos${buildListQueryString(filters)}`),
  });
}

export function useAdelanto(id: string | undefined) {
  return useQuery<ApiResponse<Adelanto>>({
    queryKey: adelantosKeys.detail(id ?? ''),
    queryFn: () => apiGet<Adelanto>(`/v1/adelantos/${id}`),
    enabled: Boolean(id),
  });
}

function isOfflineNetworkError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (err instanceof TypeError && err.message.toLowerCase().includes('fetch')) return true;
  return false;
}

export function useCreateAdelanto() {
  const qc = useQueryClient();
  return useMutation<ApiResponse<Adelanto> | { queuedOffline: true }, Error, AdelantoCreatePayload>(
    {
      mutationKey: ['adelantos', 'create'],
      mutationFn: async (payload) => {
        const idempotencyKey = generateIdempotencyKey();
        try {
          if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            await addOutboxItem({
              endpoint: '/v1/adelantos',
              method: 'POST',
              payload,
              headers: { 'Idempotency-Key': idempotencyKey },
            });
            return { queuedOffline: true };
          }
          return await apiPost<Adelanto, AdelantoCreatePayload>('/v1/adelantos', payload, {
            idempotencyKey,
          });
        } catch (err) {
          if (isOfflineNetworkError(err)) {
            await addOutboxItem({
              endpoint: '/v1/adelantos',
              method: 'POST',
              payload,
              headers: { 'Idempotency-Key': idempotencyKey },
            });
            return { queuedOffline: true };
          }
          throw err;
        }
      },
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: adelantosKeys.all });
        void qc.invalidateQueries({ queryKey: ['outbox'] });
      },
    },
  );
}

export interface UpdateAdelantoArgs {
  id: string;
  payload: AdelantoUpdatePayload;
  etag: string;
}

export function useUpdateAdelanto() {
  const qc = useQueryClient();
  return useMutation<ApiResponse<Adelanto> | { queuedOffline: true }, Error, UpdateAdelantoArgs>({
    mutationKey: ['adelantos', 'update'],
    mutationFn: async ({ id, payload, etag }) => {
      const idempotencyKey = generateIdempotencyKey();
      const endpoint = `/v1/adelantos/${id}`;
      try {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          await addOutboxItem({
            endpoint,
            method: 'PATCH',
            payload,
            headers: { 'Idempotency-Key': idempotencyKey, 'If-Match': `"${etag}"` },
          });
          return { queuedOffline: true };
        }
        return await apiPatch<Adelanto, AdelantoUpdatePayload>(endpoint, payload, {
          idempotencyKey,
          ifMatch: etag,
        });
      } catch (err) {
        if (isOfflineNetworkError(err)) {
          await addOutboxItem({
            endpoint,
            method: 'PATCH',
            payload,
            headers: { 'Idempotency-Key': idempotencyKey, 'If-Match': `"${etag}"` },
          });
          return { queuedOffline: true };
        }
        throw err;
      }
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: adelantosKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: adelantosKeys.all });
      void qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}
